/**
 * EditBankingDetailsModal Component
 *
 * Modal shell around `BankingDetailsForm`. Owns dialog chrome and save
 * lifecycle (`saving` flag, save-error capture) and merges form data with the
 * preserved symbol fields (varSymb / constSymb / specSymb) when building the
 * mutation payload. The form itself handles field rendering, inline format
 * warnings, and "at least one field" validation via `useBankingDetailsFormState`.
 *
 * Uses `useTranslation('modules.modals.editBankingDetails')` for chrome labels.
 *
 * @example
 * ```tsx
 * <EditBankingDetailsModal
 *   open={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onSave={handleSave}
 *   bankingDetails={invoice.bankingDetails}
 *   partyName="ACME Corp s.r.o."
 * />
 * ```
 */
import { useState } from 'react';
import { Modal, Button, Icon } from '../../components';
import { useEnterSubmit } from '../../hooks';
import { useTranslation } from '../../providers/UIProvider';
import { type TestIdProps, getTestId } from '../../utils';
import type { ValidationIssue } from '../../types';
import { IBankingDetails } from '@insio/shared-types';
import { BankingDetailsForm } from '../forms/banking-details/BankingDetailsForm';
import { useBankingDetailsFormState } from '../forms/banking-details/hooks';

export interface EditBankingDetailsModalProps extends TestIdProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: IBankingDetails) => Promise<void>;
  bankingDetails?: IBankingDetails | null;
  partyName?: string;
  /** External validation issues (e.g. invoice-level cross-field checks). */
  errors?: ValidationIssue[];
}

/**
 * Modal for editing banking details.
 * Allows inline editing of all banking attributes (IBAN, SWIFT, account number, etc.)
 */
export function EditBankingDetailsModal({
  open,
  onClose,
  onSave,
  bankingDetails,
  partyName,
  errors,
  testId,
  id
}: EditBankingDetailsModalProps) {
  const t = useTranslation('modules.modals.editBankingDetails');
  const state = useBankingDetailsFormState({ open, bankingDetails });
  const [saving, setSaving] = useState(false);

  const handleClose = () => {
    if (!saving) {
      state.reset();
      onClose();
    }
  };

  const handleSave = async () => {
    if (!state.validateAtLeastOneField()) return;
    setSaving(true);
    try {
      // Build payload from edited form fields only.
      // Do NOT spread bankingDetails directly — Apollo adds __typename which is rejected by the input type.
      const updatedBankingDetails: IBankingDetails = {
        ...state.formData
      };
      await onSave(updatedBankingDetails);
      handleClose();
    } catch (error) {
      state.setFormError(error instanceof Error ? error.message : t('errors.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleEnterKeyDown = useEnterSubmit(handleSave);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      layout='vertical'
      className='w-[calc(100vw-2rem)] max-w-lg'
      testId={getTestId({ testId, id })}
    >
      <Modal.Header
        label={t('title')}
        description={partyName}
        onClose={handleClose}
        variant='white'
        icon={<Icon name='building' className='w-6 h-6 text-primary-600' />}
      />

      <Modal.Content className='p-md bg-background-muted'>
        <div onKeyDown={handleEnterKeyDown}>
          <BankingDetailsForm
            value={state.formData}
            onFieldChange={state.setField}
            swiftWarning={state.swiftWarning}
            bankCodeWarning={state.bankCodeWarning}
            ibanWarning={state.ibanWarning}
            hasAnyWarning={state.hasAnyWarning}
            formError={state.formError}
            errors={errors}
            disabled={saving}
          />
        </div>
      </Modal.Content>

      <Modal.Footer variant='white'>
        <Modal.Action>
          <Button variant='ghost' size='md' onClick={handleClose} disabled={saving}>
            {t('actions.cancel')}
          </Button>
          <Button variant='primary' size='md' onClick={handleSave} disabled={saving}>
            {saving ? t('actions.saving') : t('actions.save')}
          </Button>
        </Modal.Action>
      </Modal.Footer>
    </Modal>
  );
}

EditBankingDetailsModal.displayName = 'EditBankingDetailsModal';
